import { ConfigProvider } from 'antd';
import React from 'react';

import getAntdLocale from '../components/Translation/antdLocale';
import useTranslation from '../hooks/useTranslation';
import Overrides from '../overrides';
import antdTheme from '../styles/antdTheme';

interface Props {
  children: React.ReactNode;
}

// Centre the box on the first line of a multi-line label: anchor it to the top, then nudge it
// down by half the gap between the label's line-height and the 16px box. 1.5714 is antd's
// default line-height ratio; `em` (not the `lh` unit) keeps this working in the older
// browserslist targets that don't support `lh`.
const checkboxIconStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  marginBlockStart: 'calc((1.5714em - 16px) / 2)',
};

function isHTMLElement(node?: Node): node is HTMLElement {
  return node?.nodeType === Node.ELEMENT_NODE;
}

function findParentNodeWithSelector(node: Node, selector: string): HTMLElement | null {
  return node instanceof Element ? node.closest(selector) : null;
}

const AntdConfigProvider = ({ children }: Props) => {
  const { locale } = useTranslation();

  return (
    <ConfigProvider
      theme={antdTheme}
      checkbox={{ styles: { icon: checkboxIconStyle } }}
      locale={getAntdLocale(locale)}
      getPopupContainer={node => {
        // Try to render the popup as close to the triggering element as possible, so that the
        // popup/dropdown scrolls with the triggering element.

        // Skip table elements - let them use default container behavior
        const table = findParentNodeWithSelector(node, '.ant-table');
        if (table) {
          return document.body;
        }

        // For Select dropdown, we explicitly do not look for `.ant-select-item-option` because
        // when the dropdown has only one item, the tooltip gets cropped.
        const selectDropdown = findParentNodeWithSelector(node, '.ant-select');
        if (selectDropdown) {
          return selectDropdown;
        }

        const label = findParentNodeWithSelector(node, '.ant-form-item');
        if (label) {
          return label;
        }

        const modal = findParentNodeWithSelector(node, '.ant-modal-container');
        if (modal) {
          return modal;
        }

        if (
          findParentNodeWithSelector(
            node,
            '.ant-menu, .ant-dropdown-menu, .ant-drawer-body, .ant-popover-content',
          )
        ) {
          return document.body;
        }

        if (isHTMLElement(node?.parentNode)) {
          return node.parentNode;
        }

        return document.body;
      }}
    >
      <Overrides />
      {children}
    </ConfigProvider>
  );
};

export default AntdConfigProvider;
